import { useState } from "react";

import { usePrimaryShare, useSavePrimaryShare } from "@/api/queries";
import { useCan } from "@/auth/member";
import { Button } from "@/components/ui/button";
import { FieldLabel, Input } from "@/components/ui/input";
import { Callout, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";

/** Mirrors core/picking.py:PRIMARY_SHARE_RANGE; the API is the authority, this flags a bad edit before Save. */
const MIN = 1;
const MAX = 10;

/**
 * Primary vs secondary share (DESIGN §17.12). When a request names secondary stacks, a primary stack
 * gets this many times more of the technical questions than a secondary one — unless the caller sends
 * its own weights. `/pick` reads the saved value on every call, so a save applies to the next pick.
 */
export function PrimarySharePanel() {
  const canManage = useCan("manage_picker");
  const saved = usePrimaryShare();
  const save = useSavePrimaryShare();
  const [draft, setDraft] = useState<string | null>(null);

  const server = saved.data?.primary_share_weight;
  const text = draft ?? (server === undefined ? "" : String(server));
  const value = Number(text);
  const valid = text.trim() !== "" && Number.isFinite(value) && value >= MIN && value <= MAX;
  const dirty = draft !== null && valid && value !== server;
  // What the picker does with 8 technical questions over one primary and one secondary stack
  // (core/picking.py:split_counts): whole questions, the remainder going to the primary one.
  const secondaryOf8 = valid ? Math.floor(8 / (value + 1)) : 0;

  return (
    <Panel>
      <PanelHeader title="Primary vs secondary share" />
      <div className="space-y-3 p-4">
        <p className="text-bank-muted text-sm">
          When a request names secondary stacks, each primary stack gets this many times more of the technical
          questions than a secondary one. A request that sends its own weights is not affected. A save applies
          to the next pick.
        </p>
        <ErrorNote error={saved.error} />
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-32">
            <FieldLabel htmlFor="primary-share">Primary weight</FieldLabel>
            <Input
              id="primary-share"
              type="number"
              min={MIN}
              max={MAX}
              step={0.5}
              disabled={!canManage || saved.isLoading}
              value={text}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
          {valid && (
            <p className="text-bank-muted pb-1.5 text-sm">
              : 1 secondary — with one primary and one secondary stack, 8 technical questions split{" "}
              <span className="font-mono">
                {8 - secondaryOf8} / {secondaryOf8}
              </span>
              .
            </p>
          )}
        </div>
        {!valid && text !== "" && (
          <Callout tone="bad">
            Enter a number from {MIN} to {MAX}.
          </Callout>
        )}
        <ErrorNote error={save.error} />
        {canManage ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <Button
              size="sm"
              variant="ghost"
              disabled={draft === null || save.isPending}
              onClick={() => {
                setDraft(null);
                save.reset();
              }}
            >
              Discard
            </Button>
            <Button
              size="sm"
              variant="primary"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate(value, { onSuccess: () => setDraft(null) })}
            >
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            {saved.data?.is_default ? (
              <span className="text-bank-muted ml-2 font-mono text-[11px]">default from the config file</span>
            ) : (
              saved.data?.updated_by && (
                <span className="text-bank-muted ml-2 font-mono text-[11px]">
                  last changed by {saved.data.updated_by}
                </span>
              )
            )}
          </div>
        ) : (
          <p className="text-bank-muted text-xs">Only admins can change this value.</p>
        )}
        {saved.data?.problem && <Callout tone="attention">{saved.data.problem}</Callout>}
      </div>
    </Panel>
  );
}
